import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { API_DOCS } from './ListinoForniDettaglioPage.api';

// ── Interfacce ────────────────────────────────────────────────────────────────

interface ImportListino {
  id: number;
  societaId: number;
  etichetta: string;
  note: string | null;
  stato: 'IN_CORSO' | 'COMPLETATO' | 'COMPLETATO_CON_ERRORI' | 'ERRORE';
  importatoAt: string;
  totaleRighe: number;
  righeCreate: number;
  righeAggiornate: number;
  righeErrore: number;
  strategiaAggiornamento?: 'SOLO_PREZZO' | 'PREZZO_E_ANAGRAFICA';
  fornitore: { id: number; ragioneSociale: string | null; nome: string | null };
  utente: { id: number; nome: string; email: string };
}

interface RigaListino {
  id: number;
  societaId: number;
  importListinoId: number;
  codiceArticolo: string;
  tipologia: string | null;
  descrizioneBase: string;
  descrizioneEstesa: string | null;
  umVendita: string;
  umAcquisto: string | null;
  convertitore: number | null;
  marca: string | null;
  collezione: string | null;
  formato: string | null;
  categoriaMerceologica: string | null;
  categoriaConfigurazione: string | null;
  effetto: string | null;
  prezzoStandard: number;
  qtaMinOrdinabile: number | null;
  qtaPerPallet: number | null;
  stato: 'DA_IMPORTARE' | 'IMPORTATO' | 'AGGIORNATO' | 'ERRORE';
  erroreDettaglio: string | null;
}

interface PrevalidaRiga {
  id: number;
  numero: number;
  codiceArticolo: string;
  tipo: 'errore' | 'avviso' | 'valido';
  colonna: string | null;
  valore: string | null;
  problema: string | null;
  suggerimento: string | null;
}

interface PrevalidaRisultato {
  totale: number;
  validi: number;
  errori: number;
  avvisi: number;
  righe: PrevalidaRiga[];
}

const TIPOLOGIE = [
  'PRODOTTO_CONFIGURATO',
  'ARTICOLO_FISICO',
  'LAVORAZIONE_PRESTAZIONE',
  'LICENZA',
  'CANONE',
  'INTERCAMBIABILE',
  'RAGGRUPPAMENTO_PREZZO_CORPO',
];

const PASSI_VALIDAZIONE = [
  'Lettura struttura file',
  'Verifica colonne obbligatorie',
  'Controllo campi ENUM',
  'Controllo campi tabellati',
  'Verifica Collezione → Marca coerente',
  'Controllo duplicati',
  'Completato',
];

// ── Helper badge ──────────────────────────────────────────────────────────────

const badgeStatoImport = (stato: string) => {
  const map: Record<string, string> = {
    COMPLETATO:             'bop-badge-success',
    COMPLETATO_CON_ERRORI:  'bop-badge-warning',
    ERRORE:                 'bop-badge-danger',
    IN_CORSO:               'bop-badge-neutral',
  };
  const labels: Record<string, string> = {
    COMPLETATO:            'Completato',
    COMPLETATO_CON_ERRORI: 'Con errori',
    ERRORE:                'Errore',
    IN_CORSO:              'In corso',
  };
  return (
    <span className={`bop-badge ${map[stato] || 'bop-badge-neutral'}`}>
      {labels[stato] || stato}
    </span>
  );
};

const badgeStatoRiga = (stato: string) => {
  const map: Record<string, string> = {
    DA_IMPORTARE: 'bop-badge-neutral',
    IMPORTATO:    'bop-badge-success',
    AGGIORNATO:   'bop-badge-warning',
    ERRORE:       'bop-badge-danger',
  };
  const labels: Record<string, string> = {
    DA_IMPORTARE: 'Da importare',
    IMPORTATO:    'Importato',
    AGGIORNATO:   'Aggiornato',
    ERRORE:       'Errore',
  };
  return (
    <span className={`bop-badge ${map[stato] || 'bop-badge-neutral'}`}>
      {labels[stato] || stato}
    </span>
  );
};

// ── Componente principale ─────────────────────────────────────────────────────

export default function ListinoForniDettaglioPage() {
  const { id } = useParams<{ id: string }>();
  const importId = parseInt(id || '0', 10);

  // ── Stato lista ──────────────────────────────────────────────────────────────
  const [imp,         setImp]         = useState<ImportListino | null>(null);
  const [righe,       setRighe]       = useState<RigaListino[]>([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(50);
  const [loading,     setLoading]     = useState(false);
  const [loadingImp,  setLoadingImp]  = useState(true);
  const [importing,   setImporting]   = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Filtri lista
  const [search,                  setSearch]                  = useState('');
  const [tipologia,               setTipologia]               = useState('');
  const [collezione,              setCollezione]              = useState('');
  const [categoriaMerceologica,   setCategoriaMerceologica]   = useState('');
  const [categoriaConfigurazione, setCategoriaConfigurazione] = useState('');
  const [effetto,                 setEffetto]                 = useState('');

  // ── Stato pre-validazione ────────────────────────────────────────────────────
  const [validazioneStato,    setValidazioneStato]    = useState<'idle' | 'loading' | 'done'>('idle');
  const [validazioneRisultato, setValidazioneRisultato] = useState<PrevalidaRisultato | null>(null);
  const [validazioneStep,     setValidazioneStep]     = useState(0);
  const [validazioneFiltro,   setValidazioneFiltro]   = useState<'tutti' | 'errori' | 'avvisi' | 'validi'>('tutti');
  const [validazioneSearch,   setValidazioneSearch]   = useState('');
  const validazioneIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }

  // ── Carica header import ──────────────────────────────────────────────────────
  const caricaImport = useCallback(async () => {
    if (!importId) return;
    setLoadingImp(true);
    try {
      // @api GET /api/anagrafiche/listini/:id -> {"success":true,"data":"ImportListino"}
      const res = await apiFetch<{ success: boolean; data: ImportListino }>(
        `/api/anagrafiche/listini/${importId}`
      );
      setImp(res.data);
    } catch (err) {
      console.error('[ListinoForniDettaglio] caricaImport', err);
    } finally {
      setLoadingImp(false);
    }
  }, [importId]);

  useEffect(() => { caricaImport(); }, [caricaImport]);

  // ── Carica righe ─────────────────────────────────────────────────────────────
  const caricaRighe = useCallback(async (p: number, l: number) => {
    if (!importId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(l) });
      if (search)                  params.set('search',                  search);
      if (tipologia)               params.set('tipologia',               tipologia);
      if (collezione)              params.set('collezione',              collezione);
      if (categoriaMerceologica)   params.set('categoriaMerceologica',   categoriaMerceologica);
      if (categoriaConfigurazione) params.set('categoriaConfigurazione', categoriaConfigurazione);
      if (effetto)                 params.set('effetto',                 effetto);
      // @api GET /api/anagrafiche/listini/:id/righe?page=1&limit=50&search=&tipologia=&collezione=&categoriaMerceologica=&categoriaConfigurazione=&effetto= -> {"success":true,"data":"ListinoFornitore[]","total":"number","page":"number","limit":"number"}
      const res = await apiFetch<{ success: boolean; data: RigaListino[]; total: number; page: number; limit: number }>(
        `/api/anagrafiche/listini/${importId}/righe?${params}`
      );
      setRighe(res.data);
      setTotal(res.total);
      setPage(p);
    } catch (err) {
      console.error('[ListinoForniDettaglio] caricaRighe', err);
    } finally {
      setLoading(false);
    }
  }, [importId, search, tipologia, collezione, categoriaMerceologica, categoriaConfigurazione, effetto]);

  useEffect(() => { caricaRighe(1, limit); }, [importId]); // eslint-disable-line

  // ── Checkbox ─────────────────────────────────────────────────────────────────
  const daImportareVisibili = useMemo(() => righe.filter(r => r.stato === 'DA_IMPORTARE'), [righe]);
  const tuttiSelezionati = daImportareVisibili.length > 0 && daImportareVisibili.every(r => selectedIds.has(r.id));

  const toggleSelectAll = () => {
    if (tuttiSelezionati) setSelectedIds(new Set());
    else setSelectedIds(new Set(daImportareVisibili.map(r => r.id)));
  };
  const toggleSelect = (rowId: number) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(rowId) ? n.delete(rowId) : n.add(rowId); return n; });
  };

  // ── Import ────────────────────────────────────────────────────────────────────
  const handleImportaTutti = async () => {
    if (!window.confirm(
      'Verranno importati tutti gli articoli con stato DA_IMPORTARE.\n' +
      'Gli articoli esistenti verranno aggiornati nel prezzo. Continuare?'
    )) return;
    setImporting(true);
    try {
      // @api POST /api/anagrafiche/listini/:id/importa {"tutti":"boolean","righeIds?":"number[]"} -> {"success":true,"data":{"elaborate":"number","create":"number","aggiornate":"number","errori":"number"}}
      const res = await apiFetch<{ success: boolean; data: { elaborate: number; create: number; aggiornate: number; errori: number } }>(
        `/api/anagrafiche/listini/${importId}/importa`,
        { method: 'POST', body: JSON.stringify({ tutti: true }) }
      );
      const d = res.data;
      showToast(`Elaborate: ${d.elaborate} | Create: ${d.create} | Aggiornate: ${d.aggiornate} | Errori: ${d.errori}`);
      setSelectedIds(new Set());
      if (validazioneStato === 'done') handleResetValidazione();
      await caricaRighe(page, limit);
      await caricaImport();
    } catch (err: any) {
      showToast(err.message || 'Errore durante l\'import', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleImportaSelezionati = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Verranno importati ${ids.length} articoli selezionati. Continuare?`)) return;
    setImporting(true);
    try {
      // @api POST /api/anagrafiche/listini/:id/importa {"tutti":"boolean","righeIds?":"number[]"} -> {"success":true,"data":{"elaborate":"number","create":"number","aggiornate":"number","errori":"number"}}
      const res = await apiFetch<{ success: boolean; data: { elaborate: number; create: number; aggiornate: number; errori: number } }>(
        `/api/anagrafiche/listini/${importId}/importa`,
        { method: 'POST', body: JSON.stringify({ tutti: false, righeIds: ids }) }
      );
      const d = res.data;
      showToast(`Elaborate: ${d.elaborate} | Create: ${d.create} | Aggiornate: ${d.aggiornate} | Errori: ${d.errori}`);
      setSelectedIds(new Set());
      await caricaRighe(page, limit);
      await caricaImport();
    } catch (err: any) {
      showToast(err.message || 'Errore durante l\'import', 'error');
    } finally {
      setImporting(false);
    }
  };

  // ── Pre-validazione ───────────────────────────────────────────────────────────
  const handlePrevalida = async () => {
    setValidazioneStato('loading');
    setValidazioneStep(0);

    let currentStep = 0;
    validazioneIntervalRef.current = setInterval(() => {
      currentStep++;
      if (currentStep < PASSI_VALIDAZIONE.length - 1) {
        setValidazioneStep(currentStep);
      } else {
        if (validazioneIntervalRef.current) clearInterval(validazioneIntervalRef.current);
      }
    }, 600);

    try {
      // @api GET /api/anagrafiche/listini/:id/pre-valida -> {"success":true,"data":{"totale":"number","validi":"number","errori":"number","avvisi":"number","righe":"PrevalidaRiga[]"}}
      const res = await apiFetch<{ success: boolean; data: PrevalidaRisultato }>(
        `/api/anagrafiche/listini/${importId}/pre-valida`
      );
      if (validazioneIntervalRef.current) clearInterval(validazioneIntervalRef.current);
      setValidazioneStep(PASSI_VALIDAZIONE.length - 1);
      setTimeout(() => {
        setValidazioneRisultato(res.data);
        setValidazioneStato('done');
        setValidazioneFiltro('tutti');
        setValidazioneSearch('');
      }, 500);
    } catch (err: any) {
      if (validazioneIntervalRef.current) clearInterval(validazioneIntervalRef.current);
      showToast('Errore pre-validazione: ' + err.message, 'error');
      setValidazioneStato('idle');
    }
  };

  const handleResetValidazione = () => {
    if (validazioneIntervalRef.current) clearInterval(validazioneIntervalRef.current);
    setValidazioneStato('idle');
    setValidazioneRisultato(null);
    setValidazioneStep(0);
  };

  const filteredRighe = useMemo(() => {
    if (!validazioneRisultato) return [];
    let rows = validazioneRisultato.righe;
    if      (validazioneFiltro === 'errori') rows = rows.filter(r => r.tipo === 'errore');
    else if (validazioneFiltro === 'avvisi') rows = rows.filter(r => r.tipo === 'avviso');
    else if (validazioneFiltro === 'validi') rows = rows.filter(r => r.tipo === 'valido');
    if (validazioneSearch.trim()) {
      const s = validazioneSearch.trim().toLowerCase();
      rows = rows.filter(r =>
        String(r.numero).includes(s) ||
        (r.codiceArticolo || '').toLowerCase().includes(s) ||
        (r.colonna        || '').toLowerCase().includes(s) ||
        (r.valore         || '').toLowerCase().includes(s) ||
        (r.problema       || '').toLowerCase().includes(s) ||
        (r.suggerimento   || '').toLowerCase().includes(s)
      );
    }
    return rows;
  }, [validazioneRisultato, validazioneFiltro, validazioneSearch]);

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loadingImp) return (
    <div className="listino-dettaglio-standalone">
      <div className="bop-empty-state">Caricamento...</div>
    </div>
  );

  if (!imp) return (
    <div className="listino-dettaglio-standalone">
      <div className="bop-empty-state">Importazione non trovata</div>
    </div>
  );

  return (
    <div className="listino-dettaglio-standalone">
      {/* Toast */}
      {toast && (
        <div className={`listino-dettaglio-toast ${toast.type === 'success' ? 'listino-dettaglio-toast-success' : 'listino-dettaglio-toast-error'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="listino-dettaglio-standalone-header">
        <div className="listino-dettaglio-standalone-title">
          <h1>{imp.etichetta} — {imp.fornitore?.ragioneSociale || imp.fornitore?.nome || 'Fornitore'}</h1>
          {badgeStatoImport(imp.stato)}
          {imp.strategiaAggiornamento && (
            <span
              className={`bop-badge ${imp.strategiaAggiornamento === 'PREZZO_E_ANAGRAFICA' ? 'bop-badge-warning' : 'bop-badge-info'}`}
              title={
                imp.strategiaAggiornamento === 'PREZZO_E_ANAGRAFICA'
                  ? 'Gli articoli esistenti avranno prezzo + dati anagrafici sovrascritti'
                  : 'Gli articoli esistenti avranno solo il prezzo aggiornato'
              }
            >
              {imp.strategiaAggiornamento === 'PREZZO_E_ANAGRAFICA'
                ? 'Prezzo + anagrafica'
                : 'Solo prezzo'}
            </span>
          )}
        </div>
        <div className="listino-dettaglio-header-azioni">
          {validazioneStato === 'idle' && (
            <button className="bop-btn-secondary" onClick={handlePrevalida}>
              ⚙ Pre-valida
            </button>
          )}
          <button className="bop-btn-secondary" onClick={() => window.close()}>
            ← Chiudi scheda
          </button>
        </div>
      </div>

      <div className="listino-dettaglio-content">

        {/* ── STATO A: lista normale ─────────────────────────────────────── */}
        {validazioneStato === 'idle' && (<>
          {/* Filtri */}
          <div className="bop-card">
            <div className="bop-filter-bar">
              <div className="bop-form-group">
                <label className="bop-label">Ricerca</label>
                <input type="text" className="bop-input" placeholder="Codice o descrizione..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Tipologia</label>
                <select className="bop-select" value={tipologia} onChange={e => setTipologia(e.target.value)}>
                  <option value="">Tutte</option>
                  {TIPOLOGIE.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Collezione</label>
                <input type="text" className="bop-input" placeholder="Collezione..." value={collezione} onChange={e => setCollezione(e.target.value)} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Cat. merceologica</label>
                <input type="text" className="bop-input" placeholder="Categoria merceologica..." value={categoriaMerceologica} onChange={e => setCategoriaMerceologica(e.target.value)} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Cat. configurazione</label>
                <input type="text" className="bop-input" placeholder="Categoria configurazione..." value={categoriaConfigurazione} onChange={e => setCategoriaConfigurazione(e.target.value)} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Effetto</label>
                <input type="text" className="bop-input" placeholder="Effetto..." value={effetto} onChange={e => setEffetto(e.target.value)} />
              </div>
              <div className="bop-form-group listino-dettaglio-filtri-azioni">
                <button className="bop-btn-secondary" onClick={() => { setSearch(''); setTipologia(''); setCollezione(''); setCategoriaMerceologica(''); setCategoriaConfigurazione(''); setEffetto(''); setTimeout(() => caricaRighe(1, limit), 0); }}>
                  Reset
                </button>
                <button className="bop-btn-primary" onClick={() => caricaRighe(1, limit)}>Cerca</button>
              </div>
            </div>
          </div>

          {/* Azioni massive */}
          <div className="bop-card">
            <div className="listino-dettaglio-azioni-massive">
              <button className="bop-btn-primary" onClick={handleImportaTutti} disabled={importing}>
                {importing ? '⏳ Import in corso...' : 'Importa tutti'}
              </button>
              <button className="bop-btn-secondary" onClick={handleImportaSelezionati} disabled={importing || selectedIds.size === 0}>
                Importa selezionati ({selectedIds.size})
              </button>
            </div>
          </div>

          {/* Tabella */}
          <div className="bop-card">
            {loading ? (
              <div className="bop-empty-state">Caricamento...</div>
            ) : righe.length === 0 ? (
              <div className="bop-empty-state">Nessuna riga trovata</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th><input type="checkbox" checked={tuttiSelezionati} onChange={toggleSelectAll} disabled={daImportareVisibili.length === 0} /></th>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Tipologia</th>
                    <th>U.M.</th>
                    <th>Marca</th>
                    <th>Collezione</th>
                    <th>Prezzo</th>
                    <th>Stato</th>
                    <th>Errore</th>
                  </tr>
                </thead>
                <tbody>
                  {righe.map(riga => (
                    <tr key={riga.id}>
                      <td>
                        {riga.stato === 'DA_IMPORTARE'
                          ? <input type="checkbox" checked={selectedIds.has(riga.id)} onChange={() => toggleSelect(riga.id)} />
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>{riga.codiceArticolo}</td>
                      <td>{riga.descrizioneBase}</td>
                      <td>{riga.tipologia || '-'}</td>
                      <td>{riga.umVendita}</td>
                      <td>{riga.marca || '-'}</td>
                      <td>{riga.collezione || '-'}</td>
                      <td className="bop-text-right">€ {Number(riga.prezzoStandard).toFixed(2)}</td>
                      <td>{badgeStatoRiga(riga.stato)}</td>
                      <td className="listino-dettaglio-errore-cell" title={riga.erroreDettaglio || ''}>
                        {riga.erroreDettaglio ? (riga.erroreDettaglio.length > 60 ? riga.erroreDettaglio.substring(0, 60) + '...' : riga.erroreDettaglio) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* Paginazione */}
            <div className="listino-dettaglio-paginazione">
              <span className="bop-text-muted">Righe per pagina:</span>
              <select className="bop-select" value={limit} onChange={e => { const n = parseInt(e.target.value, 10); setLimit(n); caricaRighe(1, n); }}>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
              {total > 0 && (<>
                <span className="bop-text-muted">{(page - 1) * limit + 1}–{Math.min(page * limit, total)} di {total}</span>
                <button className="bop-btn-secondary" disabled={page <= 1} onClick={() => caricaRighe(page - 1, limit)}>← Prec</button>
                <button className="bop-btn-secondary" disabled={page * limit >= total} onClick={() => caricaRighe(page + 1, limit)}>Succ →</button>
              </>)}
            </div>
          </div>
        </>)}

        {/* ── STATO B: animazione validazione ────────────────────────────── */}
        {validazioneStato === 'loading' && (
          <div className="bop-card listino-prevalida-loading-card">
            <div className="listino-prevalida-loading-title">Pre-validazione in corso…</div>
            <div className="listino-prevalida-loading-subtitle">Analisi delle righe del listino</div>
            <div className="listino-prevalida-step-list">
              {PASSI_VALIDAZIONE.map((passo, idx) => {
                const isDone   = idx < validazioneStep;
                const isActive = idx === validazioneStep;
                return (
                  <div key={idx} className={`listino-prevalida-step ${isDone ? 'listino-prevalida-step--done' : isActive ? 'listino-prevalida-step--active' : 'listino-prevalida-step--pending'}`}>
                    <span className="listino-prevalida-step-icon">
                      {isDone   ? <span className="listino-prevalida-check">✓</span>
                      : isActive ? <span className="listino-prevalida-spinner" />
                      :            <span className="listino-prevalida-circle">○</span>}
                    </span>
                    <span className="listino-prevalida-step-label">{passo}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STATO C: risultati ─────────────────────────────────────────── */}
        {validazioneStato === 'done' && validazioneRisultato && (<>
          {/* Summary + header */}
          <div className="bop-card">
            <div className="listino-prevalida-risultati-header">
              <div className="listino-prevalida-risultati-titolo">Risultati pre-validazione</div>
              <button className="bop-btn-secondary" onClick={handleResetValidazione}>← Nuovo file</button>
            </div>
            <div className="listino-prevalida-metriche">
              <div className="listino-prevalida-metrica listino-prevalida-metrica--neutral">
                <div className="listino-prevalida-metrica-valore">{validazioneRisultato.totale}</div>
                <div className="listino-prevalida-metrica-label">Totale</div>
              </div>
              <div className="listino-prevalida-metrica listino-prevalida-metrica--success">
                <div className="listino-prevalida-metrica-valore">{validazioneRisultato.validi}</div>
                <div className="listino-prevalida-metrica-label">Validi</div>
              </div>
              <div className="listino-prevalida-metrica listino-prevalida-metrica--danger">
                <div className="listino-prevalida-metrica-valore">{validazioneRisultato.errori}</div>
                <div className="listino-prevalida-metrica-label">Errori</div>
              </div>
              <div className="listino-prevalida-metrica listino-prevalida-metrica--warning">
                <div className="listino-prevalida-metrica-valore">{validazioneRisultato.avvisi}</div>
                <div className="listino-prevalida-metrica-label">Avvisi</div>
              </div>
            </div>
          </div>

          {/* Filtri + tabella */}
          <div className="bop-card">
            <div className="listino-prevalida-filtri-bar">
              <div className="listino-prevalida-pills">
                {([
                  { val: 'tutti',   label: `Tutti (${validazioneRisultato.totale})` },
                  { val: 'errori',  label: `Solo errori (${validazioneRisultato.errori})` },
                  { val: 'avvisi',  label: `Solo avvisi (${validazioneRisultato.avvisi})` },
                  { val: 'validi',  label: `Solo validi (${validazioneRisultato.validi})` },
                ] as const).map(p => (
                  <button
                    key={p.val}
                    className={`listino-prevalida-pill ${validazioneFiltro === p.val ? 'listino-prevalida-pill--active' : ''}`}
                    onClick={() => setValidazioneFiltro(p.val)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                className="bop-input listino-prevalida-search"
                placeholder="Cerca riga, colonna, valore, messaggio…"
                value={validazioneSearch}
                onChange={e => setValidazioneSearch(e.target.value)}
              />
            </div>

            {filteredRighe.length === 0 ? (
              <div className="bop-empty-state">Nessun risultato</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Riga</th>
                    <th>Codice</th>
                    <th>Colonna</th>
                    <th>Valore inserito</th>
                    <th>Problema</th>
                    <th>Suggerimento</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRighe.map((r, idx) => (
                    <tr key={`${r.id}-${idx}`} className={`listino-prevalida-row--${r.tipo}`}>
                      <td className="bop-text-center">{r.numero}</td>
                      <td>{r.codiceArticolo || '-'}</td>
                      <td>{r.colonna   ? <code className="listino-prevalida-code">{r.colonna}</code>   : '-'}</td>
                      <td>{r.valore    ? <code className="listino-prevalida-code">{r.valore}</code>    : '-'}</td>
                      <td>{r.problema  || '-'}</td>
                      <td>
                        {r.suggerimento
                          ? (
                            <button
                              className="listino-prevalida-chip-copia"
                              title="Clicca per copiare"
                              onClick={() => {
                                navigator.clipboard.writeText(r.suggerimento!).then(() => showToast('Copiato negli appunti'));
                              }}
                            >
                              {r.suggerimento}
                            </button>
                          )
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Action bar */}
          <div className="bop-card">
            <div className="listino-prevalida-action-bar">
              <div className={`listino-prevalida-action-label ${validazioneRisultato.errori > 0 ? 'listino-prevalida-action-label--danger' : validazioneRisultato.avvisi > 0 ? 'listino-prevalida-action-label--warning' : 'listino-prevalida-action-label--success'}`}>
                {validazioneRisultato.errori > 0
                  ? `${validazioneRisultato.errori} errori bloccanti — correggere il file prima di importare`
                  : validazioneRisultato.avvisi > 0
                    ? `Nessun errore bloccante — ${validazioneRisultato.avvisi} avvisi non bloccanti`
                    : 'Tutto ok — pronto per l\'import'}
              </div>
              <div className="listino-prevalida-action-buttons">
                {validazioneRisultato.errori > 0 && (
                  <button className="bop-btn-danger" onClick={() => alert('coming soon')}>
                    Esporta errori
                  </button>
                )}
                <button
                  className="bop-btn-primary"
                  onClick={handleImportaTutti}
                  disabled={validazioneRisultato.errori > 0 || importing}
                >
                  {importing ? '⏳ Import in corso...' : '▶ Avvia import'}
                </button>
              </div>
            </div>
          </div>
        </>)}

        <ApiInspector docs={API_DOCS} />
      </div>
    </div>
  );
}
